/**
 * PlatformFinanceHub — generic Finance sub-hub for a single platform key.
 *
 * Used for platforms (fitness / cgm_coin / community / mediadocs) that don't
 * have their own dedicated finance layer. Presents an "Create Invoice" card
 * (and future finance modules) that leads to the invoice list page for that
 * platform.
 *
 * The platform key is inferred from the route (:platform param).
 */
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText } from 'lucide-react';

import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { useLanguage } from '../../context/LanguageContext';
import { FINANCE_PLATFORM_META } from '../../constants/financePlatforms';
import { usePerms } from '../../permissions/PermissionsContext';

export default function PlatformFinanceHub() {
  const { language } = useLanguage();
  const { platform } = useParams();
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const meta = FINANCE_PLATFORM_META[platform];

  useEffect(() => { window.scrollTo(0, 0); }, []);

  if (!meta) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <p className="text-white/60">{t('منصة غير معروفة', 'Unknown platform')}</p>
      </div>
    );
  }

  const items = [
    {
      id: 'create-invoice',
      titleAr: 'إنشاء فاتورة',
      titleEn: 'Create Invoice',
      descAr: 'إنشاء الفواتير وتحصيل المدفوعات لهذه المنصة',
      descEn: 'Create invoices and collect payments for this platform',
      icon: FileText,
      route: `/dashboard/finance/pcol/${platform}`,
      color: meta.color,
      testid: `platform-hub-create-invoice-${platform}`,
    },
  ];

  return (
    <div className="min-h-screen bg-[#030303] relative">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full blur-[150px]"
             style={{ backgroundColor: `${meta.color}0d` }} />
      </div>

      <DashboardHeader showBackButton backPath="/dashboard/finance" />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'المالية',      label_en: 'Finance',   path: '/dashboard/finance' },
            { label_ar: isAr ? meta.ar : meta.en, label_en: meta.en, path: null },
          ]} />
        </div>
      </div>

      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }} />
            <span className="text-white/50 text-sm">{isAr ? meta.ar : meta.en}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            {t('الإدارة المالية', 'Financial Management')}
          </h1>
          <p className="text-white/50 text-sm">
            {t('اختر القسم للمتابعة', 'Choose a module to continue')}
          </p>
        </div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {visiblePages(items, 'route').map((it) => {
            const Icon = it.icon;
            return (
              <motion.button
                key={it.id}
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                whileHover={{ y: -6, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(it.route)}
                className="group relative bg-white/[0.02] backdrop-blur-xl border rounded-2xl p-6 text-start transition-all overflow-hidden"
                style={{ borderColor: `${it.color}30` }}
                data-testid={it.testid}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center border transition-transform group-hover:scale-110 shrink-0"
                    style={{ backgroundColor: `${it.color}15`, borderColor: `${it.color}40` }}
                  >
                    <Icon className="h-7 w-7" style={{ color: it.color }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-1">
                      {isAr ? it.titleAr : it.titleEn}
                    </h3>
                    <p className="text-sm text-[#A1A1AA] leading-relaxed">
                      {isAr ? it.descAr : it.descEn}
                    </p>
                  </div>
                </div>
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{ background: `radial-gradient(circle at 50% 50%, ${it.color}10 0%, transparent 70%)` }}
                />
              </motion.button>
            );
          })}
        </motion.div>
      </main>
    </div>
  );
}
